// tslint:disable: no-inferrable-types
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatButtonToggleChange } from '@angular/material/button-toggle';

import { WorkflowEditorStore } from './lib/workflow-editor.store';
import { BackendIntegrationService, BackendObjectRef } from './lib/backend-integration.service';
import { BackendIntegrationSerializer } from './lib/backend-integration-serializer';
import { WorkflowCreateFormResult } from './workflow-create-form/workflow-create-form.component';
import { State } from './lib/state.model';
import { Transition } from './lib/transition.model';
import { Workflow } from './lib/workflow.model';
import { WorkflowAuditResult } from './workflow-audit';
import { MatDialogRef, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { WorkflowEditFormComponent, WorkflowEditFormResult, WorkflowEditFormOptions } from '@fry/booking/workflow-editor/workflow-edit-form/workflow-edit-form.component';

export type WorkflowEditorSection = 'workflow-transitions' |
                                    'workflow-states' |
                                    'workflow-form' |
                                    'workflow-review' |
                                    'workflow-create';

@Component({
    selector: 'eas-workflow-editor',
    templateUrl: './workflow-editor.component.html',
    styleUrls: ['./workflow-editor.component.css'],
    providers: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkflowEditorComponent implements OnInit, OnDestroy {

  public auditResult?: WorkflowAuditResult;

  public error?: Error;

  public get hasError(): boolean {
    return this.error !== undefined;
  }

  public get isReady(): boolean {
    return this.integration.isReady;
  }

  public get workflow(): Workflow|undefined {
    return this.store.workflow;
  }

  public get availableRoles(): BackendObjectRef[] {
    return this.integration.availableRoles;
  }

  public visibleSection: WorkflowEditorSection = 'workflow-transitions';

  private destroyed$ = new Subject<void>();

  private editDialogRef?: MatDialogRef<WorkflowEditFormComponent, WorkflowEditFormResult>

  /**
   * Indicates if workflow can/should be saved
   *
   * We can save workflow when it has some changes and an Integration operation
   * is not in progress.
   *
   * @deprecated Since the introduction of the local pools, this fuctionality is
   *             retained (in case we want to push it upstream eventually) but not used
   *             throughout the editor.
   */
  public canSaveChanges$ = combineLatest([this.store.isDirty$, this.integration.isBusy$])
                           .pipe(
                             map(update => {
                               const [isDirty, isBusy] = update;
                               return isDirty && !isBusy;
                             }),
                           );

  constructor(private route: ActivatedRoute,
              private cdRef: ChangeDetectorRef,
              private integration: BackendIntegrationService,
              private store: WorkflowEditorStore,
              private dialog: MatDialog
              ) { }

  ngOnInit() {
    this.error = undefined;
    this.integration.isReady$.pipe(takeUntil(this.destroyed$))
                             .subscribe(isReady => {
      if (!isReady) { return; }

      try {
        // If we're editing existing workflow we just initialize the EditorStore
        // otherwise it will be initialized in the WorkflowCreateComponent
        if (!this.integration.workflow.isNew()) {
          const workflow =
            BackendIntegrationSerializer.deserializeWorkflow(this.integration.workflow,
                                                             this.integration.availableRoles,
                                                             this.integration.availableActions,
                                                             this.integration.availableGuards,
                                                             this.integration.availableGeneralFields,
                                                             this.integration.availableUserFields);
          this.store.initialize(workflow,
                                this.integration.availableRoles,
                                this.integration.availableActions,
                                this.integration.availableGuards,
                                this.integration.availableGeneralFields,
                                this.integration.availableUserFields,
                                false);
        }

        // Set initial editor section
        this.visibleSection = this.visibleSectionFromFragment();

        this.cdRef.markForCheck();
      } catch (error) {
        this.handleError(error);
      }
    });

    // When workflow changes we must run change detection
    // When there is a new audit we need to update UI
    combineLatest([
      this.store.auditResult$,
      this.store.workflow$
    ]).pipe(takeUntil(this.destroyed$))
      .subscribe(results => {
        this.auditResult = results[0];
        this.cdRef.markForCheck();
      });

    // We switch between different editor sections based on the URL fragments
    this.route.fragment
              .pipe(takeUntil(this.destroyed$))
              .subscribe(fragment => {
                if (!this.isReady) { return; }
                this.visibleSection = this.visibleSectionFromFragment(fragment);
                this.cdRef.markForCheck();
              });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private visibleSectionFromFragment(fragment?: string|null): WorkflowEditorSection {
    switch (fragment) {
      case 'form':
        return 'workflow-form';
      case 'review':
        return 'workflow-review';
      case 'transitions':
         return 'workflow-transitions';
      case 'states':
         return 'workflow-states';
      default:
        return this.store.workflow === undefined
              ? 'workflow-create'
              : 'workflow-transitions';
    }
  }

  private handleError(error: Error): void {
    if (error instanceof Error) {
      console.error(error);
      this.error = error;
    } else {
      this.error = new Error(`${error}`);
    }
    this.cdRef.markForCheck();
  }

  //
  // Actions
  //

  public onCreate(value: WorkflowCreateFormResult): void {
    // Create new Workflow with initial Transition & State
    const roles = this.availableRoles.filter(r => {
      return value.selectedRoles.find(rid => rid === r.id);
    });
    if (roles.length !== value.selectedRoles.length) {
      throw new Error($localize `Can't create new Workflow without a role!`);
    }

    const initialState = new State(undefined, value.state, false);
    const initialTransition = Transition.initialTransition(initialState, roles, []);
    const workflow = new Workflow(undefined,
                                  value.name,
                                  [initialState],
                                  undefined,
                                  initialTransition);
    this.integration.updateSerializedWorkflow(workflow);
    this.store.initialize(workflow,
                          this.integration.availableRoles,
                          this.integration.availableActions,
                          this.integration.availableGuards,
                          this.integration.availableGeneralFields,
                          this.integration.availableUserFields,
                          false)
    this.visibleSection = this.visibleSectionFromFragment();
  }

  public onSectionChange(change: MatButtonToggleChange) {
    this.visibleSection = this.visibleSectionFromFragment(change.value);
  }

  public onEdit() {
    if (this.editDialogRef) { return; }
    if (this.workflow === undefined) { return; }

    const options: MatDialogConfig<WorkflowEditFormOptions> = {
      hasBackdrop: true,
      minWidth: '50vw',
      id: 'workflow-editor-form-dialog',
      data: { 'workflow': this.workflow }
    };

    this.editDialogRef =
      this.dialog.open<WorkflowEditFormComponent,
                       WorkflowEditFormOptions,
                       WorkflowEditFormResult>(WorkflowEditFormComponent, options);
    this.editDialogRef.afterClosed().subscribe(result => {
      this.editDialogRef = undefined;
      if (result === undefined) { return; }
      if (this.workflow === undefined) { return; }
      this.workflow.name = result.name;
      this.store.workflow = this.workflow;
    });
  }

  //
  // Template helpers
  //

  public shouldShowSection(section: WorkflowEditorSection): boolean {
    return this.visibleSection === section;
  }

  getWorkflowName() {
    return this.workflow?.name.length > 0 ? this.workflow.name : $localize `Name not provided`;
  }
}
